import { useCallback } from "react";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {
  BulkResolveNotificationsRowResult,
  BulkResolveNotificationsRowStatus,
} from "@event-planner/shared";
import {
  notificationImportSpec,
  type ParsedNotificationImportRow,
} from "@event-planner/shared/imports";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../components/imports/ResolveAndImportDialog";
import { type BulkImportResultBucket } from "../../components/BulkImportResultTable";
import {
  bulkCreateNotifications,
  bulkResolveNotifications,
} from "../../services/notificationService";
import { dateTimeLocalToTimestamp } from "../../utils/timestamps";

interface ExcelNotificationImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** IANA timezone - local datetime strings get converted to UTC against this. */
  timezone: string;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type NotificationProposal = ProposalRow<
  ParsedNotificationImportRow,
  BulkResolveNotificationsRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkCreateNotifications>>;

export function defaultIncludeForNotificationStatus(
  status: BulkResolveNotificationsRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "unknown_audience":
    case "invalid_schedule":
    case "failed":
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Bulk Notifications import. Wraps the shared `ResolveAndImportDialog`
 * shell with notification-specific resolve + write callables. Each row's
 * audience reference is looked up against this event's audiences server-
 * side; the dialog surfaces the matched audience name (or the
 * `unknown_audience` failure) before any write.
 *
 * Local-time -> UTC conversion happens admin-side via
 * `dateTimeLocalToTimestamp`. The same converted ISO is sent to both the
 * resolve and write callables.
 */
export function ExcelNotificationImportDialog({
  open,
  onClose,
  eventId,
  timezone,
  onImported,
}: ExcelNotificationImportDialogProps) {
  const buildCallableRow = useCallback(
    (r: ParsedNotificationImportRow) => {
      let scheduledAt: string | null = null;
      if (r.scheduleMode === "scheduled" && r.scheduledAtLocal) {
        scheduledAt = dateTimeLocalToTimestamp(r.scheduledAtLocal, timezone)
          .toDate()
          .toISOString();
      }
      return {
        title: r.title,
        body: r.body,
        audience: r.audience,
        channels: r.channels,
        scheduleMode: r.scheduleMode,
        scheduledAt,
        deepLink: r.deepLink,
        emailSubject: r.emailSubject,
        emailBodyHtml: r.emailBodyHtml,
      };
    },
    [timezone],
  );

  const resolveRows = useCallback(
    async (rows: ParsedNotificationImportRow[]) => {
      const response = await bulkResolveNotifications({
        eventId,
        rows: rows.map(buildCallableRow),
      });
      return response.results;
    },
    [eventId, buildCallableRow],
  );

  const writeRows = useCallback(
    async (rows: ParsedNotificationImportRow[]) => {
      return bulkCreateNotifications({
        eventId,
        rows: rows.map(buildCallableRow),
      });
    },
    [eventId, buildCallableRow],
  );

  const renderProposalCard = useCallback(
    (proposal: NotificationProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedNotificationImportRow,
      BulkResolveNotificationsRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Notifications from Excel"
      entityNoun={{ singular: "Notification", plural: "Notifications" }}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) with one row per notification.
          Required: <strong>title</strong>, <strong>body</strong>,{" "}
          <strong>audience</strong> (an audience name for this event),{" "}
          <strong>channels</strong>, and <strong>scheduleMode</strong>.
          Audience names are resolved server-side against this event's
          audiences.
        </>
      }
      testIdSlug="notification"
      spec={notificationImportSpec}
      templateFileName="notification-import-template.xlsx"
      sheetName="Notifications"
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForNotificationStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildNotificationBuckets(
          writeResult,
          includedProposals,
          skippedProposals,
        )
      }
      importedAnythingFromResult={(result) => result.summary.created > 0}
    />
  );
}

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: NotificationProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;
  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {parsed.data.title || <em>(missing title)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Stack spacing={0.5} flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {resolution.typedTitle}
            </Typography>
            <StatusChip status={resolution.status} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Audience:{" "}
            {resolution.proposal?.audienceName ?? resolution.typedAudience}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {parsed.data.channels.map((c) => (
              <Chip key={c} size="small" label={c} variant="outlined" />
            ))}
            <Chip
              size="small"
              label={
                parsed.data.scheduleMode === "now"
                  ? "Send now"
                  : `Scheduled: ${parsed.data.scheduledAtLocal ?? ""}`
              }
              variant="outlined"
            />
          </Stack>
          {resolution.message && (
            <Typography variant="caption" color="warning.main">
              {resolution.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function StatusChip({ status }: { status: BulkResolveNotificationsRowStatus }) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "unknown_audience":
      return <Chip size="small" label="Unknown audience" color="warning" />;
    case "invalid_schedule":
      return <Chip size="small" label="Invalid schedule" color="warning" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

interface ResultRow {
  lineNumber: number;
  title: string;
  status: "success" | "unknown_audience" | "invalid_schedule" | "failed" | "skipped" | "invalid";
  message: string | null;
}

function buildNotificationBuckets(
  writeResult: WriteResult | null,
  includedProposals: NotificationProposal[],
  skippedProposals: NotificationProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        title: p.parsed.data.title,
        status: "failed",
        message: "Skipped at write phase",
      };
    }
    const status: ResultRow["status"] =
      r.status === "created"
        ? "success"
        : r.status === "unknown_audience"
          ? "unknown_audience"
          : r.status === "invalid_schedule"
            ? "invalid_schedule"
            : "failed";
    return {
      lineNumber: p.lineNumber,
      title: p.parsed.data.title,
      status,
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        title: p.parsed.data.title || "(no title)",
        status: "invalid",
        message: p.parsed.message ?? "Format error",
      };
    }
    return {
      lineNumber: p.lineNumber,
      title: p.resolution.typedTitle,
      status: "skipped",
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );
  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      rows: all
        .filter((r) => r.status === "success")
        .map((r) => ({
          id: `s-${r.lineNumber}`,
          primary: r.title,
          secondary: `Row ${r.lineNumber}`,
        })),
    },
    {
      id: "unknown_audience",
      label: "Unknown Audience",
      color: "warning",
      description:
        "The audience name didn't match any audience in this event. Create the audience on the Groups page first, then re-import.",
      rows: all
        .filter((r) => r.status === "unknown_audience")
        .map((r) => ({
          id: `u-${r.lineNumber}`,
          primary: r.title,
          secondary: `Row ${r.lineNumber}: ${r.message ?? ""}`,
        })),
    },
    {
      id: "invalid_schedule",
      label: "Invalid Schedule",
      color: "warning",
      description:
        "scheduledAt is required when scheduleMode is 'scheduled' (or 'later').",
      rows: all
        .filter((r) => r.status === "invalid_schedule")
        .map((r) => ({
          id: `i-${r.lineNumber}`,
          primary: r.title,
          secondary: `Row ${r.lineNumber}: ${r.message ?? ""}`,
        })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      description: "Rows the organizer chose not to import.",
      rows: all
        .filter((r) => r.status === "skipped")
        .map((r) => ({
          id: `k-${r.lineNumber}`,
          primary: r.title,
          secondary: `Row ${r.lineNumber}${r.message ? `: ${r.message}` : ""}`,
        })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: all
        .filter((r) => r.status === "failed" || r.status === "invalid")
        .map((r) => ({
          id: `f-${r.lineNumber}`,
          primary: r.title,
          secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
        })),
    },
  ];
}
